import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getProductsByCategory } from '../../services/categoryService';
import { formatPrice } from '../../lib/utils';
import ProductDetailsModal from './ProductDetailsModal';
import bg19 from '../../assets/images/background/bg-19.png';
import bg20 from '../../assets/images/background/bg-20.png';
import separator from '../../assets/images/icons/separator.svg';

// Fallback image if product has no images
const PLACEHOLDER_IMAGE = 'https://placehold.co/100x100?text=No+Image';

const decodeHtml = (html) => {
  if (typeof document === 'undefined') return html;
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

function MenuCategorySection({ category, index }) {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  
  // Modal state
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const sectionRef = useRef(null);
  
  // Refs to prevent API spam and handle race conditions
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  const isRightAligned = index % 2 === 0;
  const bgImage = isRightAligned ? bg19 : bg20;
  const alignmentClass = isRightAligned ? 'right-bg' : 'left-bg';

  const PAGE_SIZE = 6;

  useEffect(() => {
    const fetchProducts = async () => {
      // Abort previous pending request if any (Cleanup)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      loadingRef.current = true;

      try {
        const result = await getProductsByCategory(category.id, page, PAGE_SIZE); 
        
        // If aborted during fetch, ignore result
        if (controller.signal.aborted) return;

        if (result.items.length === 0) {
          setHasMore(false);
        } else {
          setProducts(prev => {
            // Avoid duplicates just in case
            const newItems = result.items.filter(item => !prev.some(p => p.id === item.id));
            return [...prev, ...newItems];
          });
          
          if (result.items.length < PAGE_SIZE) {
            setHasMore(false);
          }
        }
      } catch {
        if (controller.signal.aborted) return;
        // Do not disable hasMore on error, allow user to try again
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          loadingRef.current = false;
          abortControllerRef.current = null;
        }
      }
    };

    fetchProducts();

    // Cleanup on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [page, category.id]);

  const handleLoadMore = (e) => {
    e.preventDefault();
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleProductClick = (e, productId) => {
    e.preventDefault();
    setSelectedProductId(productId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProductId(null);
  };

  // Split products into two columns
  const leftColumnProducts = products.filter((_, i) => i % 2 === 0);
  const rightColumnProducts = products.filter((_, i) => i % 2 !== 0);
  
  return (
    <section className="menu-two" ref={sectionRef} style={{ minHeight: '200px' }}>
      <div className={alignmentClass}>
        <img src={bgImage} alt="" title="" />
      </div>
      <div className="auto-container">
        <div className="title-box centered">
          <div className="subtitle">
            <span>{category.description || 'Naš Meni'}</span>
          </div>
          <div className="pattern-image">
            <img src={separator} alt="separator" />
          </div>
          <h2>{category.name}</h2>
        </div>
        <div className="row clearfix">
          {/* Left Column */}
          <div className="menu-col col-lg-6 col-md-12 col-sm-12">
            <div className="inner">
              {leftColumnProducts.map(product => (
                <div className="dish-block" key={product.id}>
                  <div className="inner-box">
                    <div className="dish-image">
                      <Link to="#" onClick={(e) => handleProductClick(e, product.id)}>
                        <img 
                          src={product.image_urls && product.image_urls.length > 0 ? product.image_urls[0] : PLACEHOLDER_IMAGE} 
                          alt={product.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%' }} // Inline style to ensure consistency if CSS is missing
                        />
                      </Link>
                    </div>
                    <div className="title clearfix">
                      <div className="ttl clearfix">
                        <h5>
                          <Link to="#" onClick={(e) => handleProductClick(e, product.id)}>{product.name}</Link>
                        </h5>
                      </div>
                      <div className="price">
                        <span>
                          {product.retail_price?.amount 
                            ? formatPrice(product.retail_price.amount, product.retail_price.currency_code || 'USD') 
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="text desc">
                      <Link to="#" onClick={(e) => handleProductClick(e, product.id)}>
                        {product.description ? (
                          <span dangerouslySetInnerHTML={{ __html: decodeHtml(product.description) }} />
                        ) : (
                          'Opis će biti dostupan uskoro.'
                        )}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column */}
          <div className="menu-col col-lg-6 col-md-12 col-sm-12">
            <div className="inner">
              {rightColumnProducts.map(product => (
                <div className="dish-block" key={product.id}>
                  <div className="inner-box">
                    <div className="dish-image">
                      <Link to="#" onClick={(e) => handleProductClick(e, product.id)}>
                        <img 
                          src={product.image_urls && product.image_urls.length > 0 ? product.image_urls[0] : PLACEHOLDER_IMAGE} 
                          alt={product.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%' }}
                        />
                      </Link>
                    </div>
                    <div className="title clearfix">
                      <div className="ttl clearfix">
                        <h5>
                          <Link to="#" onClick={(e) => handleProductClick(e, product.id)}>{product.name}</Link>
                        </h5>
                      </div>
                      <div className="price">
                        <span>
                          {product.retail_price?.amount 
                            ? formatPrice(product.retail_price.amount, product.retail_price.currency_code || 'USD') 
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="text desc">
                      <Link to="#" onClick={(e) => handleProductClick(e, product.id)}>
                        {product.description ? (
                          <span dangerouslySetInnerHTML={{ __html: decodeHtml(product.description) }} />
                        ) : (
                          'Opis će biti dostupan uskoro.'
                        )}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Load More Button */}
        {hasMore && (
          <div className="link-box centered" style={{ marginTop: '40px', textAlign: 'center' }}>
            <Link 
                to="#" 
                onClick={handleLoadMore}
                className="theme-btn btn-style-two clearfix"
                style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.7 : 1 }}
            >
                <span className="btn-wrap">
                    <span className="text-one">{loading ? 'Učitavanje...' : 'Prikaži još'}</span>
                    <span className="text-two">{loading ? 'Učitavanje...' : 'Prikaži još'}</span>
                </span>
            </Link>
          </div>
        )}

        <ProductDetailsModal 
          isOpen={isModalOpen} 
          onClose={handleCloseModal} 
          productId={selectedProductId} 
        />
      </div>
    </section>
  );
}

export default MenuCategorySection;
